<template>
    <div>
        <!-- Modal -->
        <div
            :class="['modal', 'slide-in-top', { 'd-block': visible }]"
            aria-modal="true"
            aria-describedby="shareDynamicPricingModalContent"
            aria-labelledby="shareDynamicPricingModalTitle"
            id="shareDynamicPricingModal"
            tabindex="-1"
            role="dialog"
            @click.self="closeModal"
            data-test="share-pricing-modal"
        >
            <div class="modal-dialog modal-lg" role="document" id="shareDynamicPricingModalContent">
                <div class="modal-content">
                    <header class="modal-header d-flex">
                        <button class="btn btn-close ml-auto" @click.prevent="closeModal" data-test="share-modal-close">
                            <i class="fal fa-fw fa-times"></i>
                        </button>
                    </header>
                    <div id="shareDynamicPricingModalBody" class="modal-body" data-test="share-modal-body">
                        <!-- Loading state -->
                        <div v-if="isLoading" class="text-center p-4" data-test="share-loading">
                            <i class="fas fa-spinner fa-spin"></i> Loading pricing data...
                        </div>

                        <!-- Pricing data display -->
                        <div v-else-if="pricingData.length > 0" class="view-prices" data-test="pricing-summary-list">
                            <div v-for="modifier in pricingData" :key="`${modifier.year}-${modifier.month}`" class="price row" data-test="pricing-month-group">
                                <div class="col-3 text-right">
                                    <div>
                                        <strong data-test="month-name">{{ modifier.month }}</strong>
                                    </div>
                                    <div data-test="year">{{ modifier.year }}</div>
                                </div>
                                <div class="col-9">
                                    <table data-test="modifiers-table">
                                        <tr v-for="row in modifier.modifiers" :key="`${row.month}-${row.day}`" data-test="modifier-row">
                                            <td class="pb-3 pr-4">
                                                <strong data-test="modifier-date">{{ row.month }} {{ row.day }}</strong>
                                                <div class="nowrap" data-test="modifier-title">{{ row.title }}</div>
                                            </td>
                                            <td class="pb-3 pr-4" data-test="modifier-percentage">{{ row.modifier }}%</td>
                                        </tr>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <!-- No data message -->
                        <div v-else-if="!isLoading" class="text-center p-4" data-test="no-pricing-data">
                            <p>No dynamic pricing data available for your current schedule.</p>
                        </div>

                        <!-- Email form -->
                        <div class="form" v-if="pricingData.length > 0" data-test="share-email-form">
                            <form @submit.prevent="send">
                                <div class="form-group">
                                    <input
                                        v-model="email"
                                        type="email"
                                        name="email"
                                        placeholder="Send To"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.email }"
                                        :disabled="isSending"
                                        required
                                        data-test="share-email-input"
                                    />
                                    <div v-if="errors.email" class="invalid-feedback" data-test="share-email-error">
                                        {{ errors.email[0] }}
                                    </div>
                                </div>
                                <div class="form-group text-right">
                                    <button
                                        type="submit"
                                        class="btn btn-secondary mx-1"
                                        :disabled="isSending || !email"
                                        data-test="share-submit-btn"
                                    >
                                        <span v-if="isSending">
                                            <i class="fas fa-spinner fa-spin"></i> Sending...
                                        </span>
                                        <span v-else>Submit</span>
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-cancel mx-1"
                                        @click.prevent="closeModal"
                                        :disabled="isSending"
                                        data-test="share-cancel-btn"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal backdrop -->
        <div v-if="visible" class="modal-backdrop fade show" @click.prevent="closeModal"></div>

        <!-- Trigger button -->
        <button
            class="btn btn-secondary mb-4 w-100 dropdown-toggle"
            type="button"
            @click="openModal"
            :disabled="isLoading"
            data-test="view-share-btn"
        >
            View and Share Dynamically Priced Days
        </button>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'ShareDynamicPricing',
    data() {
        return {
            visible: false,
            email: '',
            pricingData: [],
            isLoading: false,
            isSending: false,
            errors: {}
        };
    },
    mounted() {
        // Listen for events to reload pricing data
        window.Bus.$on('reloadDynamicPricing', this.fetchPricingData);
    },
    beforeDestroy() {
        window.Bus.$off('reloadDynamicPricing', this.fetchPricingData);
    },
    methods: {
        async openModal() {
            this.visible = true;
            await this.fetchPricingData();
        },

        closeModal() {
            this.visible = false;
            this.email = '';
            this.errors = {};
        },

        async fetchPricingData() {
            this.isLoading = true;
            this.errors = {};

            try {
                const response = await axios.get('/api/team/price-modifiers/share-data');
                this.pricingData = response.data.data || [];
            } catch (error) {
                console.error('Error fetching pricing data:', error);
                this.$messenger.error('Failed to load pricing data. Please try again.');
                this.pricingData = [];
            } finally {
                this.isLoading = false;
            }
        },

        async send() {
            if (!this.email || this.isSending) {
                return;
            }

            this.isSending = true;
            this.errors = {};

            try {
                await axios.post('/api/team/price-modifiers/share', {
                    email: this.email
                });

                this.$messenger.success('Email sent successfully!');
                this.closeModal();

                // Emit event for any components that need to know
                window.Bus.$emit('dynamicPricingShared', { email: this.email });
            } catch (error) {
                console.error('Error sending email:', error);

                if (error.response && error.response.status === 422) {
                    // Validation errors
                    this.errors = error.response.data.errors || {};
                } else {
                    this.$messenger.error('Failed to send email. Please try again.');
                }
            } finally {
                this.isSending = false;
            }
        }
    }
};
</script>

<style scoped>
.modal {
    z-index: 1050;
}

.modal-backdrop {
    z-index: 1040;
}

.nowrap {
    white-space: nowrap;
}

.slide-in-top {
    animation: slide-in-top 0.5s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
}

@keyframes slide-in-top {
    0% {
        transform: translateY(-1000px);
        opacity: 0;
    }
    100% {
        transform: translateY(0);
        opacity: 1;
    }
}
</style>